import { useLayoutEffect, useRef, type HTMLAttributes } from 'react';

type EmailHtmlViewProps = Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'dangerouslySetInnerHTML'> & {
  /** Must already have passed through the rendering site's existing sanitizer. */
  html: string;
  enhanceImages: boolean;
};

function safeOriginalImageUrl(source: string, baseUrl: string): string | null {
  if (!source.trim() || /[\u0000-\u001f\u007f\\]/.test(source)) return null;
  try {
    const url = new URL(source, baseUrl);
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null;
    // Local images use the existing static media route, not arbitrary app/API
    // routes that could have side effects when opened in a new tab.
    if (url.origin === new URL(baseUrl).origin && !/^\/uploads\/images\/[^/]+$/.test(url.pathname)) {
      return null;
    }
    return url.href;
  } catch {
    return null;
  }
}

/**
 * Read-only DOM enhancement, after sanitization. Images stay in their original
 * paragraphs/tables with their styles intact; controls are adjacent siblings,
 * never wrappers or children of an email's existing link.
 */
export function EmailHtmlView({ html, enhanceImages, ...props }: EmailHtmlViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !enhanceImages) return;
    const document = container.ownerDocument;
    let disposed = false;
    const cleanups: Array<() => void> = [];

    container.querySelectorAll('img').forEach((originalImage, index) => {
      const originalSource = originalImage.getAttribute('src');
      const originalSrcSet = originalImage.getAttribute('srcset');
      if (!originalSource && !originalSrcSet) return;

      let image = originalImage;
      let removeImageListeners = () => {};
      const label = image.alt.trim() || `Inline image ${index + 1}`;
      const controls = document.createElement('span');
      controls.className = 'not-prose inline-flex max-w-full flex-wrap items-center gap-2 mx-1 align-middle text-xs';
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', label);
      const status = document.createElement('span');
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = 'Retry';
      retry.className = 'rounded border border-current px-2 py-1 underline focus-visible:outline focus-visible:outline-2';
      retry.setAttribute('aria-label', `Retry image: ${label}`);
      const openOriginal = document.createElement('a');
      openOriginal.textContent = 'Open original image';
      openOriginal.className = 'underline';
      openOriginal.target = '_blank';
      openOriginal.rel = 'noopener noreferrer';
      openOriginal.setAttribute('aria-label', `Open original image: ${label}`);

      const anchor = image.closest('a');
      const placement = anchor && container.contains(anchor) ? anchor : image;
      placement.after(controls);

      const render = (state: 'pending' | 'loaded' | 'error') => {
        if (disposed) return;
        status.textContent = state === 'pending' ? 'Image loading…' : 'Image failed to load.';
        status.setAttribute('role', state === 'error' ? 'alert' : 'status');
        if (state === 'loaded') status.remove();
        else if (!controls.contains(status)) controls.prepend(status);
        if (state === 'error') {
          if (!controls.contains(retry)) controls.append(retry);
        } else retry.remove();
        // currentSrc accounts for a browser-selected srcset candidate. Do not
        // fall back to another URL when the selected source is data/blob/cid.
        const href = safeOriginalImageUrl(image.currentSrc || originalSource || '', document.baseURI);
        if (href) {
          openOriginal.href = href;
          if (!controls.contains(openOriginal)) controls.append(openOriginal);
        } else openOriginal.remove();
        controls.hidden = state === 'loaded' && !href;
      };

      const observe = () => {
        const observedImage = image;
        const onLoad = () => {
          if (image === observedImage) render('loaded');
        };
        const onError = () => {
          if (image === observedImage) render('error');
        };
        observedImage.addEventListener('load', onLoad);
        observedImage.addEventListener('error', onError);
        removeImageListeners = () => {
          observedImage.removeEventListener('load', onLoad);
          observedImage.removeEventListener('error', onError);
        };
      };
      const checkComplete = () => {
        if (!disposed && image.complete) render(image.naturalWidth > 0 ? 'loaded' : 'error');
      };
      const onRetry = (event: MouseEvent) => {
        // Only our button handles retries; the email's original anchors retain
        // their own destinations and click behavior.
        event.stopPropagation();
        if (disposed) return;
        removeImageListeners();
        const replacement = image.cloneNode(false) as HTMLImageElement;
        replacement.removeAttribute('src');
        replacement.removeAttribute('srcset');
        image.replaceWith(replacement);
        image = replacement;
        observe();
        render('pending');
        // A fresh element starts a real browser image attempt, even after a
        // cached failure. Keep the exact source (including signed query strings).
        if (originalSrcSet !== null) image.setAttribute('srcset', originalSrcSet);
        if (originalSource !== null) image.setAttribute('src', originalSource);
        queueMicrotask(checkComplete);
      };
      retry.addEventListener('click', onRetry);
      // Register first, then check: cached images can finish before the effect,
      // or between listener registration and the complete/naturalWidth check.
      observe();
      render('pending');
      checkComplete();
      cleanups.push(() => {
        removeImageListeners();
        retry.removeEventListener('click', onRetry);
        controls.remove();
      });
    });

    return () => {
      disposed = true;
      cleanups.forEach(cleanup => cleanup());
    };
  }, [html, enhanceImages]);

  return <div {...props} ref={containerRef} dangerouslySetInnerHTML={{ __html: html }} />;
}